import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { EInteropSystem } from "@calaosoft/osapp-common/interoperabilite/models/einterop-system";
import { ObservableProperty } from "@calaosoft/osapp-common/observable/models/observable-property";
import { secure } from "@calaosoft/osapp-common/rxjs/operators/secure";
import { tapAll } from "@calaosoft/osapp-common/rxjs/operators/tap-all";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import { ModalController } from "@ionic/angular";
import { Observable, catchError, forkJoin, mergeMap, of, tap, throwError } from "rxjs";
import { ModalComponentBase } from "../../../modules/modal";
import { HasPermissions } from "../../../modules/permissions/decorators/has-permissions.decorator";
import { EPermissionScopes } from "@calaosoft/osapp-common/permissions/models/epermission-scopes";
import { IHasPermission, PermissionsService } from "../../../modules/permissions/services/permissions.service";
import { ShowMessageParamsPopup } from "../../../services/interfaces/ShowMessageParamsPopup";
import { ISubscriptionsInfo } from "../../../services/interfaces/isubscriptions-info";
import { InteropService } from "../../../services/interop.service";
import { PlatformService } from "../../../services/platform.service";
import { UiMessageService } from "../../../services/uiMessage.service";

type ICacheSubscriptionsInfoBySystem = Partial<Record<EInteropSystem, ISubscriptionsInfo>>;

interface IToggleInfo {
	[systemId: string]: {
		[systemType: string]: boolean;
	};
}

@Component({
	selector: "interop-modal",
	templateUrl: "./interop-modal.component.html",
	styleUrls: ["./interop-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class InteropModalComponent extends ModalComponentBase<void> implements OnInit, IHasPermission {
	//#region FIELDS

	private moToggle: IToggleInfo = {};

	private moCacheSub = new ObservableProperty<ICacheSubscriptionsInfoBySystem>({});

	private maSystemsIdsForMailbox: EInteropSystem[] = [];
	private maSystemsIdsForCalendar: EInteropSystem[] = [];

	//#endregion

	//#region PROPERTIES

	public interopSystem = EInteropSystem;

	public readonly observableIsLoading = new ObservableProperty<boolean>(false);

	@HasPermissions({
		permission: EInteropSystem.microsoft,
		permissionScopes: [EPermissionScopes.conversations, EPermissionScopes.interop, EPermissionScopes.mailbox]
	})
	/** Indique si l'utilisateur a accès a une interop mail Microsoft. */
	public get canInteropMicrosoftMailbox(): boolean {
		return true;
	}

	@HasPermissions({
		permission: EInteropSystem.google,
		permissionScopes: [EPermissionScopes.conversations, EPermissionScopes.interop, EPermissionScopes.mailbox]
	})
	/** Indique si l'utilisateur a accès a une interop mail Google. */
	public get canInteropGoogleMailbox(): boolean {
		return true;
	}

	public get canInteropMailbox(): boolean {
		return this.canInteropMicrosoftMailbox || this.canInteropGoogleMailbox;
	}

	@HasPermissions({
		permission: EInteropSystem.microsoft,
		permissionScopes: [EPermissionScopes.events, EPermissionScopes.interop, EPermissionScopes.calendar]
	})
	/** Indique si l'utilisateur a accès a une interop de calendrier Microsoft. */
	public get canInteropMicrosoftCalendar(): boolean {
		return true;
	}

	@HasPermissions({
		permission: EInteropSystem.google,
		permissionScopes: [EPermissionScopes.events, EPermissionScopes.interop, EPermissionScopes.calendar]
	})
	/** Indique si l'utilisateur a accès a une interop de calendrier Google. */
	public get canInteropGoogleCalendar(): boolean {
		return true;
	}

	public get canInteropCalendar(): boolean {
		return this.canInteropMicrosoftCalendar || this.canInteropGoogleCalendar;
	}

	//#endregion

	//#region METHODS

	constructor(
		public readonly isvcInterop: InteropService,
		private readonly isvcUiMessage: UiMessageService,
		public readonly isvcPermissions: PermissionsService,
		poModalCtrl: ModalController,
		psvcPlatform: PlatformService,
		poChangeDetectorRef: ChangeDetectorRef
	) {
		super(poModalCtrl, psvcPlatform, poChangeDetectorRef);
	}

	public override ngOnInit(): void {
		if (this.canInteropGoogleMailbox) this.maSystemsIdsForMailbox.push(EInteropSystem.google);
		if (this.canInteropMicrosoftMailbox) this.maSystemsIdsForMailbox.push(EInteropSystem.microsoft);

		if (this.canInteropGoogleCalendar) this.maSystemsIdsForCalendar.push(EInteropSystem.google);
		if (this.canInteropMicrosoftCalendar) this.maSystemsIdsForCalendar.push(EInteropSystem.microsoft);

		this.initSubscribeInfo();
	}

	private initSubscribeInfo(): void {
		const laSystemsIds: EInteropSystem[] = [];
		if (this.canInteropGoogleCalendar || this.canInteropGoogleMailbox) laSystemsIds.push(EInteropSystem.google);
		if (this.canInteropMicrosoftCalendar || this.canInteropMicrosoftMailbox)
			laSystemsIds.push(EInteropSystem.microsoft);

		forkJoin(
			laSystemsIds.map((peSystemId: EInteropSystem) =>
				this.isvcInterop.getSubscribeInfo$(peSystemId).pipe(
					tap((poAuthInfo: ISubscriptionsInfo) => {
						if (this.moCacheSub.value) {
							this.moCacheSub.value[peSystemId] = poAuthInfo;
						}
					})
				)
			)
		)
			.pipe(
				tap(() => this.detectChanges()),
				secure(this)
			)
			.subscribe();
	}

	public onApplyButtonClick(peSystemId: EInteropSystem): void {
		const laSystemTypeToSubscribe: string[] = [];
		const laSystemTypeToUnsubscribe: string[] = [];

		if (this.maSystemsIdsForMailbox.includes(peSystemId)) {
			const lbSubscribe: boolean = this.hasSubscribe(peSystemId, "mailbox");
			if (
				Object.keys(this.moToggle).includes(peSystemId) &&
				Object.keys(this.moToggle[peSystemId]).includes("mailbox")
			) {
				if (lbSubscribe !== this.moToggle[peSystemId]["mailbox"]) {
					lbSubscribe ? laSystemTypeToUnsubscribe.push("mailbox") : laSystemTypeToSubscribe.push("mailbox");
				}
			}
		}

		if (this.maSystemsIdsForCalendar.includes(peSystemId)) {
			const lbSubscribe: boolean = this.hasSubscribe(peSystemId, "calendar");
			if (
				Object.keys(this.moToggle).includes(peSystemId) &&
				Object.keys(this.moToggle[peSystemId]).includes("calendar")
			) {
				if (lbSubscribe !== this.moToggle[peSystemId]["calendar"]) {
					lbSubscribe ? laSystemTypeToUnsubscribe.push("calendar") : laSystemTypeToSubscribe.push("calendar");
				}
			}
		}

		if (ArrayHelper.hasElements(laSystemTypeToUnsubscribe))
			this.sendUnsubscription(peSystemId, laSystemTypeToUnsubscribe);

		if (ArrayHelper.hasElements(laSystemTypeToSubscribe)) this.sendSubscription(peSystemId, laSystemTypeToSubscribe);
	}

	private sendSubscription(psSystemId: string, paSystemToSubscribe: string[]): void {
		this.observableIsLoading.value = true;
		this.isvcInterop
			.demandAuth$(psSystemId, paSystemToSubscribe)
			.pipe(
				tap((pbResult: boolean) => {
					this.initSubscribeInfo();
					if (!pbResult) {
						this.isvcUiMessage.showPopup(
							new ShowMessageParamsPopup({
								header: "Erreur",
								message: "Impossible de vous connecter. Réessayer plus tard."
							})
						);
					}
				}),
				catchError((poError: Error) => {
					this.isvcUiMessage.showPopup(
						new ShowMessageParamsPopup({
							header: "Erreur",
							message: "Impossible de vous connecter. Réessayer plus tard."
						})
					);
					return throwError(() => poError);
				}),
				tapAll(() => (this.observableIsLoading.value = false)),
				secure(this)
			)
			.subscribe();
	}

	private sendUnsubscription(peSystemId: EInteropSystem, paSystemToUnsubscribe: string[]): void {
		this.observableIsLoading.value = true;
		this.isvcInterop
			.deleteAuth$(peSystemId, paSystemToUnsubscribe)
			.pipe(
				tap((pbResult: boolean) => {
					this.initSubscribeInfo();
					if (!pbResult) {
						this.isvcUiMessage.showPopup(
							new ShowMessageParamsPopup({
								header: "Erreur",
								message: "Impossible de vous désinscrire. Réessayer plus tard."
							})
						);
					}
				}),
				catchError((poError: Error) => {
					this.isvcUiMessage.showPopup(
						new ShowMessageParamsPopup({
							header: "Erreur",
							message: "Impossible de vous désinscrire. Réessayer plus tard."
						})
					);
					return throwError(() => poError);
				}),
				tapAll(() => (this.observableIsLoading.value = false)),
				secure(this)
			)
			.subscribe();
	}

	public onToggleChanged(psSystemId: string, psSystemType: string, pbChecked: boolean): void {
		if (!Object.keys(this.moToggle).includes(psSystemId)) this.moToggle[psSystemId] = {};
		this.moToggle[psSystemId][psSystemType] = pbChecked;
	}

	public hasSubscribe$(psSystemId: string, psSystemType: string): Observable<boolean> {
		return this.moCacheSub.value$.pipe(
			mergeMap((poValue: ICacheSubscriptionsInfoBySystem) => {
				if (poValue[psSystemId] && poValue[psSystemId][psSystemType]) {
					return of(poValue[psSystemId][psSystemType].ids.length > 0);
				}
				return of(false);
			})
		);
	}

	private hasSubscribe(peSystemId: EInteropSystem, psSystemType: string): boolean {
		const loCacheSub = this.moCacheSub.value ?? {};
		if (loCacheSub[peSystemId] && loCacheSub[peSystemId][psSystemType])
			return loCacheSub[peSystemId][psSystemType].ids.length > 0;
		return false;
	}

	public getEmailBySystemId(peSystemId: EInteropSystem): string {
		return (this.moCacheSub.value ?? {})[peSystemId]?.account?.email ?? "";
	}

	public getDisplayNameBySystemId(peSystemId: EInteropSystem): string {
		return (this.moCacheSub.value ?? {})[peSystemId]?.account?.displayName ?? "";
	}

	//#endregion
}
